import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useUpdateCampoTipoAtivo } from '@/hooks/useCamposTipoAtivo';
import { CampoTipoAtivo, CampoTipoAtivoFormData } from '@/types/tipo-ativo';

const campoSchema = z.object({
  nome_campo: z.string().min(1, 'Nome do campo é obrigatório'),
  label: z.string().min(1, 'Label é obrigatório'),
  tipo_campo: z.string().min(1, 'Tipo é obrigatório'),
  descricao: z.string().optional(),
  obrigatorio: z.boolean().optional(),
  ordem: z.number().optional(),
  tab: z.string().optional(),
  ativo: z.boolean().optional(),
  configuracoes: z.object({
    placeholder: z.string().optional(),
    opcoes: z.array(z.string()).optional(),
    min: z.number().optional(),
    max: z.number().optional(),
    sufixo: z.string().optional(),
    prefixo: z.string().optional(),
  }).optional(),
});

interface EditCampoTipoAtivoDialogProps {
  campo: CampoTipoAtivo;
  tipoAtivoId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const EditCampoTipoAtivoDialog = ({
  campo,
  tipoAtivoId,
  open,
  onOpenChange,
}: EditCampoTipoAtivoDialogProps) => {
  const updateMutation = useUpdateCampoTipoAtivo();

  const form = useForm<Partial<CampoTipoAtivoFormData>>({
    resolver: zodResolver(campoSchema),
  });

  const tipoCampoSelecionado = form.watch('tipo_campo');

  useEffect(() => {
    if (open && campo) {
      form.reset({
        nome_campo: campo.nome_campo,
        label: campo.label,
        tipo_campo: campo.tipo_campo,
        descricao: campo.descricao || '',
        obrigatorio: campo.obrigatorio,
        ordem: campo.ordem,
        tab: campo.tab,
        ativo: campo.ativo,
        configuracoes: campo.configuracoes || {},
      });
    }
  }, [open, campo, form]);

  const onSubmit = async (data: Partial<CampoTipoAtivoFormData>) => {
    await updateMutation.mutateAsync({
      id: campo.id,
      tipoAtivoId,
      data,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar Campo Customizado</DialogTitle>
          <DialogDescription>
            Altere as configurações do campo
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="label"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Label *</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="nome_campo"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome do Campo *</FormLabel>
                  <FormDescription>Nome interno (não recomendado alterar)</FormDescription>
                  <FormControl>
                    <Input {...field} disabled />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="tipo_campo"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tipo de Campo *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="text">Texto Curto</SelectItem>
                        <SelectItem value="textarea">Texto Longo</SelectItem>
                        <SelectItem value="number">Número</SelectItem>
                        <SelectItem value="date">Data</SelectItem>
                        <SelectItem value="boolean">Sim/Não</SelectItem>
                        <SelectItem value="select">Lista Suspensa</SelectItem>
                        <SelectItem value="multiselect">Múltipla Escolha</SelectItem>
                        <SelectItem value="email">E-mail</SelectItem>
                        <SelectItem value="url">URL</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="tab"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tab</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="basico">Básico</SelectItem>
                        <SelectItem value="tecnico">Técnico</SelectItem>
                        <SelectItem value="negocio">Negócio</SelectItem>
                        <SelectItem value="seguranca">Segurança</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="descricao"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descrição</FormLabel>
                  <FormControl>
                    <Textarea {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="obrigatorio"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-lg border p-4">
                    <FormLabel>Obrigatório</FormLabel>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="ativo"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-lg border p-4">
                    <FormLabel>Ativo</FormLabel>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="ordem"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Ordem</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {(tipoCampoSelecionado === 'select' || tipoCampoSelecionado === 'multiselect') && (
              <FormField
                control={form.control}
                name="configuracoes.opcoes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Opções</FormLabel>
                    <FormDescription>Uma opção por linha</FormDescription>
                    <FormControl>
                      <Textarea
                        value={field.value?.join('\n') || ''}
                        onChange={(e) => {
                          const opcoes = e.target.value.split('\n').filter(o => o.trim());
                          form.setValue('configuracoes', {
                            ...form.getValues('configuracoes'),
                            opcoes,
                          });
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {tipoCampoSelecionado === 'number' && (
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="configuracoes.sufixo"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Sufixo</FormLabel>
                      <FormControl>
                        <Input placeholder="Ex: GB, %, kg" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="configuracoes.prefixo"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Prefixo</FormLabel>
                      <FormControl>
                        <Input placeholder="Ex: R$, US$" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? 'Salvando...' : 'Salvar Alterações'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
